import { useState, useEffect, useMemo, useCallback } from 'react';
import { Row, Col, Table, Popover, Typography } from 'antd';
import Chart from 'react-apexcharts';

const { Title } = Typography;

const Statistics = ({ dbCertifications, dbTrainings, dbStudyPrograms, rsSkillsGroup, rsSpecificKnowledge, rsSpecificSkills, rsProfiles }) => {
  const [programSkills, setProgramSkills] = useState({ names: [], occurrences: [] });
  const [trainingSkills, setTrainingSkills] = useState({ names: [], occurrences: [] });
  const [certificationSkills, setCertificationSkills] = useState({ names: [], occurrences: [] });
  const [programProfiles, setProgramProfiles] = useState({ medians: [], profileCoverage: [] });
  const [trainingProfiles, setTrainingProfiles] = useState({ medians: [], profileCoverage: [] });
  const [certificationProfiles, setCertificationProfiles] = useState({ medians: [], profileCoverage: [] });

  const median = (arr) => {
    const nums = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(nums.length / 2);
    return nums.length % 2 !== 0 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2;
  };

  const percentageLevel = (percent) => (percent > 67 ? 'High' : percent > 33 ? 'Medium' : percent >= 0 ? 'Low' : 'Error');

  const computeChartSkillsData = useCallback((items = []) => {
    // Initialize counts for each skills_group ID
    const counts = rsSkillsGroup.reduce((acc, grp) => {
      acc[grp.id] = 0;
      return acc;
    }, {});

    // Count occurrences of each group across items
    items.forEach((item) => {
      const groups = Array.isArray(item.skills_group) ? item.skills_group : [];
      groups.forEach((gid) => {
        if (counts.hasOwnProperty(gid)) counts[gid]++;
      });
    });

    // Sort groups by descending count
    const sorted = Object.entries(counts).sort(([, a], [, b]) => b - a);

    return {
      names: sorted.map(([id]) => {
        const grp = rsSkillsGroup.find((g) => g.id === Number(id));
        return grp ? grp.name : id;
      }),
      occurrences: sorted.map(([, c]) => c),
    };
  }, [rsSkillsGroup]);

  const computeProfileCoverage = useCallback((items = []) => {
    if (!Array.isArray(items) || !rsProfiles.length) return { profileCoverage: [], medians: [] };

    const coverageList = items.map((item) => {
      const itemGroups = Array.isArray(item.skills_group) ? item.skills_group : [];

      const profileCoverage = rsProfiles.map((profile) => {
        const included = [];
        const missing = [];
        profile.skills_group.forEach((gid) => {
          const grpObj = rsSkillsGroup.find((g) => g.id === gid);
          const name = grpObj ? grpObj.name : gid;

          if (itemGroups.includes(gid)) {
            included.push(name);
          } else {
            missing.push(name);
          }
        });

        const percent = profile.skills_group.length ? Math.round((included.length / profile.skills_group.length) * 100) : 0;

        return { profile: profile.name, percent, included, missing };
      });

      return { ...item, profile_coverage: profileCoverage };
    });

    const medians = rsProfiles.map((p, idx) => ({
      profile: p.name,
      median: median(coverageList.map((c) => c.profile_coverage[idx].percent)),
    }));

    return { profileCoverage: coverageList, medians };
  }, [rsProfiles, rsSkillsGroup]);

  useEffect(() => {
    setProgramSkills(computeChartSkillsData(dbStudyPrograms));
    setTrainingSkills(computeChartSkillsData(dbTrainings));
    setCertificationSkills(computeChartSkillsData(dbCertifications));

    setProgramProfiles(computeProfileCoverage(dbStudyPrograms));
    setTrainingProfiles(computeProfileCoverage(dbTrainings));
    setCertificationProfiles(computeProfileCoverage(dbCertifications));
  }, [dbCertifications, dbTrainings, dbStudyPrograms, rsProfiles, rsSkillsGroup, computeChartSkillsData, computeProfileCoverage]);

  const makeColumns = useMemo(() => {
    const base = [{ title: '', dataIndex: 'label', key: 'label' }];
    const cols = rsProfiles.map((p, i) => ({
      title: p.name,
      dataIndex: `col${i}`,
      key: p.name,
      onCell: (record) => {
        const pc = record.profile_coverage.find((x) => x.profile === p.name);
        return pc && pc.percent === 100 ? { className: 'green' } : { className: 'normal' };
      },
      render: (_pct, record) => {
        const pc = record.profile_coverage.find((x) => x.profile === p.name);
        return (
          <Popover
            placement="leftBottom"
            arrowPointAtCenter
            getPopupContainer={(trigger) => trigger.parentElement}
            trigger="hover"
            content={
              <div className="skills-popover">
                <Title level={4}>{p.name}</Title>
                <Title level={5}>Included Skills</Title>
                <ul>{pc.included.length > 0 ? pc.included.map((s) => <li key={s}>{s}</li>) : <span className="empty">None</span>}</ul>
                <Title level={5}>Missing Skills</Title>
                <ul>{pc.missing.length > 0 ? pc.missing.map((s) => <li key={s}>{s}</li>) : <span className="empty">None</span>}</ul>
              </div>
            }
          >
            <div className="coverage-text-statistics">{percentageLevel(pc.percent)}</div>
          </Popover>
        );
      },
    }));
    return base.concat(cols);
  }, [rsProfiles]);

  const makeDataSource = (data) =>
    data.map((item, idx) => ({
      key: idx,
      label: item.university ? `${item.university} | ${item.name} (${item.degree})` : item.organizer ? `${item.organizer} | ${item.name}` : `${item.organization} | ${item.name}`,
      profile_coverage: item.profile_coverage,
    }));

  const renderSummary = (medians) => () =>
    (
      <Table.Summary.Row>
        <Table.Summary.Cell>Result (median)</Table.Summary.Cell>
        {medians.map((m) => (
          <Table.Summary.Cell key={m.profile}>{percentageLevel(m.median)}</Table.Summary.Cell>
        ))}
      </Table.Summary.Row>
    );

  const computeChartCoverageData = (profileState) => ({
    names: rsProfiles.map((p) => p.name),
    occurrences: profileState.medians.map((m) => m.median),
  });

  // Chart configs array: first median coverage charts, then skill occurrence charts
  const chartConfigs = [
    {
      title: 'Coverage of Enisa profile in study programs',
      data: computeChartCoverageData(programProfiles),
      color: '#FF9800',
    },
    {
      title: 'Coverage of Enisa profile in trainigs',
      data: computeChartCoverageData(trainingProfiles),
      color: '#2E93fA',
    },
    {
      title: 'Coverage of Enisa profile in certifications',
      data: computeChartCoverageData(certificationProfiles),
      color: '#66DA26',
    },
    // Skill occurrence charts
    { title: 'Skill occurrences in study programs', data: programSkills, color: '#546E7A' },
    { title: 'Skill occurrences in trainings', data: trainingSkills, color: '#E91E63' },
    { title: 'Skill occurrences in certifications', data: certificationSkills, color: '#FF9800' },
  ];

  return (
    <div className="statistics-base">
      <h3 className="section-title">Statistics</h3>
      <div className="statistics-content">
        <div className="statistics-content-scroll">
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <h5 className="statistics-section-title">Enisa profiles in study programs</h5>
              <Table
                dataSource={makeDataSource(programProfiles.profileCoverage)}
                columns={makeColumns}
                pagination={false}
                size="small"
                bordered
                summary={renderSummary(programProfiles.medians)}
                className="profiles-overview"
                tableLayout="fixed"
              />
            </Col>
            <Col span={24}>
              <h5 className="statistics-section-title">Enisa profiles in trainings</h5>
              <Table
                dataSource={makeDataSource(trainingProfiles.profileCoverage)}
                columns={makeColumns}
                pagination={false}
                size="small"
                bordered
                summary={renderSummary(trainingProfiles.medians)}
                className="profiles-overview"
                tableLayout="fixed"
              />
            </Col>
            <Col span={24}>
              <h5 className="statistics-section-title">Enisa profiles in certifications</h5>
              <Table
                dataSource={makeDataSource(certificationProfiles.profileCoverage)}
                columns={makeColumns}
                pagination={false}
                size="small"
                bordered
                summary={renderSummary(certificationProfiles.medians)}
                className="profiles-overview"
                tableLayout="fixed"
              />
            </Col>
            {chartConfigs.map(({ title, data, color }, i) => {
              const categories = data.names;
              const seriesData = data.occurrences;
              return (
                <Col span={24} key={i}>
                  <div className="chart-container">
                    <h5 className="statistics-section-title">{title}</h5>
                    <div className="chart-container-content">
                      <Chart
                        type="radar"
                        series={[{ name: title, data: seriesData }]}
                        options={{
                          colors: [color],
                          chart: { toolbar: { show: false } },
                          xaxis: { categories },
                        }}
                      />
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        </div>
      </div>
    </div>
  );
};

export default Statistics;
